import { useEffect, useMemo } from "react"
import {
  BannerWarning,
  Block,
  Button,
  LabelLarge,
  ListItem,
  Skeleton,
  useStyletron,
} from "@mezo-org/mezo-clay"

import {
  RootState,
  selectTokenDecimals,
  selectWalletBalances,
  useDappSelector,
} from "#/redux-state"
import FailedActionModalBody from "#/shared/components/modals/FailedActionModalBody"
import WaitingForActionModalBody from "#/shared/components/modals/WaitingForActionModalBody"
import { FRIENDLY_DECIMALS_AMOUNT } from "#/shared/constants"
import {
  useAccountHistory,
  useFetchWalletBalances,
  useSwapReview,
} from "#/shared/hooks"
import useSwapTransaction from "#/shared/hooks/swap/useSwapTransaction"
import { SwapFormValue } from "#/shared/types"
import { getCurrencySymbol, separateThousandsWithComma } from "#/shared/utils"

import { ComponentStepProps, useStepperModalContext } from "../StepperModal"

import SwapModalReviewHeader from "./SwapModalReviewHeader"
import SwapModalReviewItem from "./SwapModalReviewItem"

export default function SwapModalReview({
  onClickContinue,
}: ComponentStepProps) {
  const { values } = useStepperModalContext<SwapFormValue>()
  const { tokenIn, tokenOut, slippage } = values

  const {
    networkFee,
    transactionFee,
    transactionTotal,
    isSlippageTooHigh,
    isFetchingSwapFee,
  } = useSwapReview(
    tokenIn.token,
    tokenOut.token,
    tokenIn.amount,
    Number(slippage),
  )

  const [css, theme] = useStyletron()

  const tokenInDecimals = useDappSelector((state: RootState) =>
    selectTokenDecimals(state, tokenIn.token),
  )

  const tokenOutDecimals = useDappSelector((state: RootState) =>
    selectTokenDecimals(state, tokenOut.token),
  )

  const {
    swap,
    needsApproval,
    isError,
    isSwapTransactionConfirmed,
    isSwapTransactionPending,
    isWaitingForSwapConfirmation,
    isWaitingForApproval,
  } = useSwapTransaction()

  const { mBTC } = useDappSelector(selectWalletBalances)

  const { fetchWalletBalances } = useFetchWalletBalances()
  const { refetch: fetchAccountHistory } = useAccountHistory()

  useEffect(() => {
    const handleConfirmation = async () => {
      if (isSwapTransactionConfirmed) {
        onClickContinue()

        // Refetch wallet balances and transaction history
        await fetchWalletBalances({ fetchOnChainData: true, refetch: true })
        await fetchAccountHistory()
      }
    }

    handleConfirmation()
  }, [
    onClickContinue,
    isSwapTransactionConfirmed,
    fetchWalletBalances,
    fetchAccountHistory,
  ])

  const swapModalReviewHeader = useMemo(
    () => (
      <SwapModalReviewHeader
        amountIn={tokenIn.amount}
        amountOut={tokenOut.amount}
        tokenIn={tokenIn.token}
        tokenOut={tokenOut.token}
      />
    ),
    [tokenIn, tokenOut],
  )

  const swapRate = useMemo(() => {
    const { amount: tokenInAmount } = tokenIn
    const { amount: tokenOutAmount } = tokenOut

    return separateThousandsWithComma(
      Number(tokenOutAmount) / Number(tokenInAmount),
      0,
      FRIENDLY_DECIMALS_AMOUNT,
    )
  }, [tokenIn, tokenOut])

  if (isError) {
    return <FailedActionModalBody onClickNextStep={swap} />
  }

  if (isSwapTransactionPending || isWaitingForSwapConfirmation) {
    return <WaitingForActionModalBody title={swapModalReviewHeader} />
  }

  return (
    <Block
      className={css({
        padding: `${theme.sizing.scale700} ${theme.sizing.scale700}`,
        marginTop: theme.sizing.scale500,
        height: "100%",
      })}
    >
      {swapModalReviewHeader}
      <SwapModalReviewItem
        title="Swap"
        value={tokenIn.amount}
        symbol={getCurrencySymbol(tokenIn.token)}
        tokenDecimals={tokenInDecimals}
      />
      <SwapModalReviewItem
        title="Receive"
        value={tokenOut.amount}
        symbol={getCurrencySymbol(tokenOut.token)}
        tokenDecimals={tokenOutDecimals}
      />
      {swapRate && (
        <SwapModalReviewItem
          title="Rate"
          tooltip="Current rate that may differ at the time of the swap"
          value={`1 ${getCurrencySymbol(
            tokenIn.token,
          )} ~ ${swapRate} ${getCurrencySymbol(tokenOut.token)}`}
        />
      )}
      <SwapModalReviewItem
        title="Slippage"
        tooltip={<>Maximum price difference you&apos;ll accept</>}
        value={slippage}
        symbol="%"
        showWarning={isSlippageTooHigh}
      />
      <SwapModalReviewItem
        title="Current Network Fee"
        tooltip="Ensures network security and uptime"
        value={networkFee}
        symbol={getCurrencySymbol("mBTC")}
        tokenDecimals={Number(mBTC.decimals)}
      />
      <SwapModalReviewItem
        title="Transaction Fee"
        tooltip="Incentivizes a good swap rate"
        value={transactionFee}
        symbol={getCurrencySymbol(tokenIn.token)}
        isLoading={isFetchingSwapFee}
        tokenDecimals={tokenInDecimals}
      />
      <ListItem
        overrides={{
          Content: {
            style: () => ({
              borderTop: `1px solid ${theme.colors.borderOpaque}`,
              borderBottom: "none",
              minHeight: "42px",
              marginLeft: theme.sizing.scale200,
              paddingRight: 0,
              marginRight: theme.sizing.scale200,
            }),
          },
        }}
        endEnhancer={() =>
          isFetchingSwapFee ? (
            <Skeleton
              animation
              height={theme.sizing.scale800}
              width={theme.sizing.scale3200}
            />
          ) : (
            <LabelLarge>
              ${separateThousandsWithComma(transactionTotal, 2, 2)}
            </LabelLarge>
          )
        }
      >
        <LabelLarge>Total</LabelLarge>
      </ListItem>
      {isSlippageTooHigh && (
        <BannerWarning
          artworkType="icon"
          overrides={{ Root: { style: () => ({ marginInline: 0 }) } }}
        >
          Your slippage setting ({slippage}%) is high for this transaction, so
          the cost to send assets may differ from what&apos;s displayed
        </BannerWarning>
      )}
      <Button
        isLoading={isSwapTransactionPending || isWaitingForApproval}
        onClick={swap}
        size="large"
        width="100%"
        overrides={{
          BaseButton: { style: () => ({ marginTop: theme.sizing.scale200 }) },
        }}
      >
        {needsApproval ? "Approve" : "Swap"}
      </Button>
    </Block>
  )
}
